import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Bandage } from 'lucide-react';
import {
  createPatientWound,
  deletePatientWound,
  getPatientWounds,
  updatePatientWound,
} from '../../services/api';
import type {
  TaskListResponse,
  TaskStatus,
  WoundResponse,
} from '../../services/api/types.gen';
import WoundsCard from './WoundsCard';
import WoundFormDialog from './WoundFormDialog';
import WoundAssessmentsDrawer from './WoundAssessmentsDrawer';
import DeleteWoundDialog from './DeleteWoundDialog';
import { useStore } from '../../store';
import { toDateStr } from './woundUtils';
import { EDITABLE_STATUSES } from '../../constants/statuses';
import { STATUS_DISPLAY_LABELS } from '../task/TaskDetailPanel';

interface PatientWoundsProps {
  patientId: string;
  visitId?: string;
  visitStatus?: TaskStatus;
  visits?: TaskListResponse[];
  onDocumentCreated?: () => void;
  /**
   * Called after the wound set changes (add/edit, discontinue, or delete). The
   * parent uses this to refresh its cached wound count so the tab badge stays
   * correct without waiting for a tab switch (ROG-3195). Opening/closing the
   * assessments drawer is excluded — assessments move `assessment_count`, not
   * the non-discontinued wound count the badge shows.
   */
  onWoundsChanged?: () => void;
  /**
   * Set when no visit can serve as wound context, e.g. none of the patient's
   * visit types has a wound section. The wound list still renders, since wounds
   * are patient-scoped clinical history and must stay readable, and this text
   * explains the situation.
   *
   * Adding, discontinuing, and assessing stay disabled because each is
   * attributed to the visit it happened on and there is no visit here to
   * attribute to. Correcting and removing a wound name no visit, so they stay
   * available: a wound recorded on a visit type that has since lost its wound
   * section would otherwise be stranded.
   */
  noVisitContextReason?: string;
}

const PatientWounds: React.FC<PatientWoundsProps> = ({
  patientId,
  visitId,
  visitStatus,
  visits = [],
  onDocumentCreated,
  onWoundsChanged,
  noVisitContextReason,
}) => {
  // A locked visit blocks every wound write recorded against it.
  const isVisitLocked =
    !!visitId && (!visitStatus || !EDITABLE_STATUSES.has(visitStatus));
  // Adding a wound, discontinuing one, and assessing one are all attributed to a
  // visit, so they need one whose status still allows edits. With no visit there
  // is nothing to attribute to, so they stay closed.
  const canAdd = !!visitId && !isVisitLocked;
  // Correcting or removing a wound carries no visit reference: WoundUpdate has no
  // visit field and a delete names none, which is why the backend leaves both
  // ungated. So these stay open with no visit context, where the wound list is
  // otherwise read-only, and a wound recorded on a visit type that has since lost
  // its wound section can still be fixed or removed.
  const canEdit = !isVisitLocked;
  const showError = useStore(state => state.showError);
  const [wounds, setWounds] = useState<WoundResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form dialog
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingWound, setEditingWound] = useState<WoundResponse | null>(null);

  // Delete confirmation
  const [deletingWound, setDeletingWound] = useState<WoundResponse | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);

  // Assessments drawer
  const [assessmentsWound, setAssessmentsWound] =
    useState<WoundResponse | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const fetchWounds = useCallback(async () => {
    if (!visitId && !noVisitContextReason) return;
    setIsLoading(true);
    setError(null);
    try {
      const resp = await getPatientWounds({
        path: { patient_id: patientId },
        query: { include_discontinued: true },
      });
      setWounds((resp.data as WoundResponse[]) ?? []);
    } catch {
      setError('Failed to load wounds');
    } finally {
      setIsLoading(false);
    }
    // `visitId` and `noVisitContextReason` are here because the guard above
    // reads them, so switching visits refetches even though the request itself
    // is patient-scoped and returns the same rows. The redundant fetch is cheap
    // and keeps the dependency list honest, which is worth more than avoiding it.
  }, [patientId, visitId, noVisitContextReason]);

  useEffect(() => {
    fetchWounds();
  }, [fetchWounds]);

  const handleSaveWound = async (data: {
    location: string;
    wound_type: string;
    onset_date: string | null;
    body_map_point?: { x: number; y: number } | null;
    discontinued_date?: string | null;
    discontinued_reason?: string | null;
    discontinued_comments?: string | null;
  }) => {
    try {
      if (editingWound) {
        const resp = await updatePatientWound({
          path: { patient_id: patientId, wound_id: editingWound.id },
          body: {
            ...data,
            ...(visitId && data.discontinued_date
              ? { discontinued_on_visit_id: visitId }
              : {}),
          },
        });
        // Client has no throwOnError; a failed update returns { error } with no
        // data. Bail before touching state or firing the count refresh.
        if (resp.error) {
          showError('Failed to update wound. Please try again.');
          return;
        }
        const updated = resp.data as WoundResponse;
        setWounds(prev =>
          prev.map(w =>
            w.id === updated.id
              ? { ...updated, assessment_count: w.assessment_count }
              : w
          )
        );
        if (assessmentsWound?.id === updated.id)
          setAssessmentsWound(prev =>
            prev
              ? { ...updated, assessment_count: prev.assessment_count }
              : prev
          );
      } else {
        const resp = await createPatientWound({
          path: { patient_id: patientId },
          body: { ...data, added_on_visit_id: visitId! },
        });
        if (resp.error) {
          showError('Failed to create wound. Please try again.');
          return;
        }
        const created = resp.data as WoundResponse;
        setWounds(prev => [created, ...prev]);
      }
      setIsFormOpen(false);
      setEditingWound(null);
      // Creating adds to the badge count; editing can set/clear a discontinue
      // date, which moves it too.
      onWoundsChanged?.();
    } catch (err) {
      console.error('Failed to save wound:', err);
      showError(
        editingWound
          ? 'Failed to update wound. Please try again.'
          : 'Failed to create wound. Please try again.'
      );
    }
  };

  const handleDiscontinueWound = async (
    woundId: string,
    date: Date,
    reason: string,
    comments: string
  ) => {
    try {
      const resp = await updatePatientWound({
        path: { patient_id: patientId, wound_id: woundId },
        body: {
          discontinued_date: toDateStr(date),
          discontinued_reason: reason || null,
          discontinued_comments: comments.trim() || null,
          ...(visitId ? { discontinued_on_visit_id: visitId } : {}),
        },
      });
      // Client has no throwOnError; a failed discontinue returns { error }. Throw
      // so the outer catch surfaces the toast and HealWoundPopover stays open, and
      // we never fire onWoundsChanged on a failure.
      if (resp.error) throw resp.error;
      const updated = resp.data as WoundResponse;
      setWounds(prev => prev.map(w => (w.id === updated.id ? updated : w)));
      // Discontinuing drops the wound from the badge's non-discontinued count.
      onWoundsChanged?.();
    } catch (err) {
      console.error('Failed to discontinue wound:', err);
      showError('Failed to discontinue wound. Please try again.');
      throw err;
    }
  };

  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDeleteWound = async () => {
    if (!deletingWound) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const response = await deletePatientWound({
        path: { patient_id: patientId, wound_id: deletingWound.id },
      });
      // Client has no throwOnError; a failed delete returns { error }. Don't
      // optimistically remove the row or fire the count refresh on failure.
      if (response.error) {
        setDeleteError('Failed to delete wound. Please try again.');
        return;
      }
      const deletedId = deletingWound.id;
      setWounds(prev => prev.filter(w => w.id !== deletedId));
      setDeletingWound(null);
      onWoundsChanged?.();
    } catch (err) {
      console.error('Failed to delete wound:', err);
      setDeleteError('Failed to delete wound. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenAssessments = (wound: WoundResponse) => {
    setAssessmentsWound(wound);
    setIsDrawerOpen(true);
  };

  if (!visitId && !noVisitContextReason) {
    return (
      <div className='flex flex-col items-center justify-center py-12 text-muted-foreground'>
        <Bandage className='h-12 w-12 mb-4 opacity-30' />
        <p className='text-base font-medium'>Select a visit to view wounds.</p>
      </div>
    );
  }

  return (
    <>
      {noVisitContextReason && (
        <div className='mb-3 inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs text-amber-800'>
          <AlertTriangle className='h-3.5 w-3.5 flex-shrink-0 text-amber-600' />
          {noVisitContextReason}
        </div>
      )}
      {isVisitLocked && (
        <div className='mb-3 inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs text-amber-800'>
          <AlertTriangle className='h-3.5 w-3.5 flex-shrink-0 text-amber-600' />
          Editing wounds is unavailable because this visit is{' '}
          {visitStatus
            ? (STATUS_DISPLAY_LABELS[visitStatus] ?? visitStatus)
            : 'not in an editable state'}
          .
        </div>
      )}
      <WoundsCard
        patientId={patientId}
        wounds={wounds}
        isLoading={isLoading}
        error={error}
        canAdd={canAdd}
        canEdit={canEdit}
        visitId={visitId}
        onDocumentCreated={onDocumentCreated}
        onRetry={fetchWounds}
        onAddWound={() => {
          setEditingWound(null);
          setIsFormOpen(true);
        }}
        onEditWound={wound => {
          setEditingWound(wound);
          setIsFormOpen(true);
        }}
        onDeleteWound={setDeletingWound}
        onDiscontinueWound={handleDiscontinueWound}
        onOpenAssessments={handleOpenAssessments}
      />

      <WoundFormDialog
        open={isFormOpen}
        onOpenChange={open => {
          setIsFormOpen(open);
          if (!open) setEditingWound(null);
        }}
        editingWound={editingWound}
        canDiscontinue={canAdd}
        onSave={handleSaveWound}
      />

      <WoundAssessmentsDrawer
        wound={assessmentsWound}
        open={isDrawerOpen}
        readOnly={!canAdd}
        visitId={visitId}
        visits={visits}
        onOpenChange={open => {
          setIsDrawerOpen(open);
          if (!open) fetchWounds();
        }}
      />

      <DeleteWoundDialog
        wound={deletingWound}
        isDeleting={isDeleting}
        error={deleteError}
        onConfirm={handleDeleteWound}
        onClose={() => {
          setDeletingWound(null);
          setDeleteError(null);
        }}
      />
    </>
  );
};

export default PatientWounds;
